import React from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Receipt, ExternalLink, Wallet } from "lucide-react";

/**
 * Public app launcher / hub. Users land here at whateverittakes.shop.
 * Buttons deep-link to this app's own routes or external Emergent apps.
 */
const APPS = [
  {
    id: "orders",
    title: "Order Management",
    description: "Manual order placement, fulfillment, claim orders, and reports.",
    to: "/login",
    external: false,
    icon: ShoppingCart,
    accent: "from-slate-900 to-slate-700",
    testid: "hub-orders-btn",
  },
  {
    id: "salary-system",
    title: "Salary System",
    description: "Employee salary management portal.",
    to: "https://salary-system-23.emergent.host/",
    external: true,
    icon: Wallet,
    accent: "from-indigo-700 to-indigo-500",
    testid: "hub-salary-btn",
  },
  {
    id: "billing-pos",
    title: "Billing POS",
    description: "Point-of-sale billing suite for counters and retail billing.",
    to: "https://saas-business-suite-2.emergent.host/login",
    external: true,
    icon: Receipt,
    accent: "from-emerald-700 to-emerald-500",
    testid: "hub-billing-pos-btn",
  },
];

function AppTile({ app }) {
  const Icon = app.icon;
  const inner = (
    <Card className="group relative overflow-hidden p-6 h-full transition-all border border-border hover:border-foreground/20 hover:shadow-lg">
      <div className={`absolute inset-0 opacity-0 group-hover:opacity-5 bg-gradient-to-br ${app.accent} transition-opacity`} />
      <div className="relative flex flex-col h-full">
        <div className={`w-11 h-11 rounded-lg bg-gradient-to-br ${app.accent} text-white flex items-center justify-center mb-4`}>
          <Icon className="w-5 h-5" />
        </div>
        <h3 className="font-display font-semibold text-lg mb-1 flex items-center gap-1">
          {app.title}
          {app.external && <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />}
        </h3>
        <p className="text-sm text-muted-foreground flex-1">{app.description}</p>
        <Button variant="outline" size="sm" className="mt-4 w-fit" tabIndex={-1}>
          Open →
        </Button>
      </div>
    </Card>
  );
  if (app.external) {
    return (
      <a href={app.to} target="_blank" rel="noreferrer" data-testid={app.testid}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={app.to} data-testid={app.testid}>
      {inner}
    </Link>
  );
}

export default function Hub() {
  return (
    <div className="min-h-screen bg-muted/20">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12 md:py-20">
        <header className="mb-10 md:mb-14">
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">
            Welcome
          </h1>
          <p className="text-muted-foreground text-base mt-3 max-w-xl">
            Choose an app below to get started.
          </p>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 md:gap-6">
          {APPS.map((app) => <AppTile key={app.id} app={app} />)}
        </div>

        <footer className="mt-16 text-center text-xs text-muted-foreground">
          Powered by your team · {new Date().getFullYear()}
        </footer>
      </div>
    </div>
  );
}
